import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { productionReportKeyPrefix } from "@/features/production-report/hooks";
import type { LineChain, LineChainLink } from "@/types/line-chains.types";

// Spec 083 (T075) — a cadeia no cliente.
//
// ⚠️ As duas mutações invalidam TAMBÉM o prefixo do relatório, e não só a lista de cadeias. Ligar
// duas linhas muda o recorte disponível E o conteúdo da tela: a etapa nova aparece, e a divergência
// passa a existir onde não existia. Invalidar só `line-chains` deixaria o seletor certo e a tela
// velha — a pior combinação, porque parece que a ligação não funcionou.

export const lineChainsKey = ["line-chains"] as const;
export const lineChainLinksKey = (lineId: string) => ["line-chains", "of-line", lineId] as const;

export function useLineChains() {
  return useQuery<{ items: LineChain[] }>({
    queryKey: lineChainsKey,
    queryFn: () => api.get<{ items: LineChain[] }>("/api/v1/line-chains"),
  });
}

export function useUpstreamLinks(lineId: string) {
  return useQuery<{ items: LineChainLink[] }>({
    queryKey: lineChainLinksKey(lineId),
    enabled: Boolean(lineId),
    queryFn: () => api.get<{ items: LineChainLink[] }>(`/api/v1/lines/${lineId}/chain-links`),
  });
}

function useChainInvalidation(lineId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: lineChainsKey });
    void qc.invalidateQueries({ queryKey: lineChainLinksKey(lineId) });
    void qc.invalidateQueries({ queryKey: productionReportKeyPrefix });
  };
}

export function useCreateChainLink(lineId: string) {
  const invalidate = useChainInvalidation(lineId);
  return useMutation({
    mutationFn: (upstreamLineId: string) =>
      api.post<LineChainLink>(`/api/v1/lines/${lineId}/chain-links`, { upstreamLineId }),
    onSuccess: invalidate,
  });
}

export function useDeleteChainLink(lineId: string) {
  const invalidate = useChainInvalidation(lineId);
  return useMutation({
    mutationFn: (upstreamLineId: string) =>
      api.delete<void>(`/api/v1/lines/${lineId}/chain-links/${upstreamLineId}`),
    onSuccess: invalidate,
  });
}
